//standard library imports
import { useEffect, useState } from 'react';
//External library imports
import { get as lodashGet, keys as lodashKeys } from 'lodash';
import { toast } from 'react-toastify';
import { UilTrash, UilCheckCircle, UilEye } from '@iconscout/react-unicons';
import moment from 'moment';

//Internal module imports
import { useLoader, useQueryParamWithDefault, useUserRole } from '../../../../hooks';
import Typography from '../../../../components/Typography/Typography';
import Table from '../../../../components/Table/Table';
import ModalComponent from '../../../../components/ModalComponent/ModalComponent';
import DatePicker from '../../../../components/Inputs/DatePicker/DatePicker';
import { DropdownComponent } from '../../../../components/Inputs/Input';
import FilterBarClasses from '../../../../styles/Filterbar.module.css';
import { AdminService } from '../../../../services/BiddingService/CommonServices/AdminService';
import { CustomerService } from '../../../../services/BiddingService/CommonServices/CustomerService';
import { RegulationService } from '../../../../services/BiddingService/CommonServices/RegulationService';
import { momentTimeFormater } from '../../../../utils/timeHelper';
import IconComponent from '../../../../components/Icon/Icon';
import bidClasses from '../Bidding.module.css';
import { Capitalize } from '../../../../utils/stringHelper';
import { RootBidRequestService } from '../../../../services/BiddingService/Root/BidRequestService';
import RequestOverview from './ModalComponent/RequestOverview';
import DeleteModal from '../../../../components/DeleteModal/DeleteModal';
import { AdminBidRequestService } from '../../../../services/BiddingService/Admin/BidRequestService';
import { DateParam, StringParam } from 'use-query-params';
import { AdminBiddingService } from '../../../../services/BiddingService/Admin/BiddingService';

const DEFAULT_DROPDOWN_OPTION = {
    label: 'All',
    value: 'all',
};

const HEADERS_KEYS = {
    masterBidRequest: {
        headers: ['Customer Name', 'License', 'Instance', 'Start Date', 'End Date', 'Created At', 'Updated At', 'Status', 'Action'],
        keys: ['customerName', 'license', 'instance', 'startDate', 'endDate', 'createdAt', 'updatedAt', 'status', 'action'],
    },
    indivisualMarket: {
        headers: ['Customer Name', 'License', 'Instance', 'Start Date', 'End Date', 'Created At', 'Updated At', 'Status', 'Action'],
        keys: ['customerName', 'license', 'instance', 'startDate', 'endDate', 'createdAt', 'updatedAt', 'status', 'action'],
    },
};

const FILTER_OPTIONS = [
    { label: 'Current', value: 'current' },
    { label: 'Past', value: 'past' },
];

const getDropdownOption = (data = [], isDefaultOption = true, labelKey = 'name', valueKey = '_id') => {
    const temp = isDefaultOption ? [DEFAULT_DROPDOWN_OPTION] : [];
    data.map((el) => {
        temp.push({ label: lodashGet(el, labelKey), value: lodashGet(el, valueKey) });
    });
    return temp;
};

export const BidRequest = ({ indivisualMarket = { status: false, market: null }, cId }) => {
    const [initialLoad, setInitialLoad] = useState(false);

    const { isRootUser, isAdminUser, adminId } = useUserRole();
    const service = isRootUser ? RootBidRequestService : AdminBidRequestService;
    const [startLoader, stopLoader] = useLoader();
    const [date, setDate] = useQueryParamWithDefault('date', DateParam, moment().toDate(), '', {
        clearOnUnmount: false,
    });

    const [admins, setAdmins] = useState([isAdminUser ? {} : DEFAULT_DROPDOWN_OPTION]);
    const [selectedAdmin, setSelectedAdmin] = useQueryParamWithDefault('admin', StringParam, isAdminUser ? adminId : DEFAULT_DROPDOWN_OPTION.value, '', {
        clearOnUnmount: false,
    });

    const [customers, setCustomers] = useState(indivisualMarket.status ? [] : [DEFAULT_DROPDOWN_OPTION]);
    const [selectedCustomer, setSelectedCustomer] = useQueryParamWithDefault('customer', StringParam, indivisualMarket.status ? {} : DEFAULT_DROPDOWN_OPTION.value, '', {
        clearOnUnmount: false,
    });

    const [regulations, setRegulations] = useState([]);
    const [selectedRegulation, setSelectedRegulation] = useQueryParamWithDefault('market', StringParam, indivisualMarket.status ? indivisualMarket.market : {}, '', {
        clearOnUnmount: false,
    });

    const [selectedFilter, setSelectedFilter] = useQueryParamWithDefault('filter', StringParam, FILTER_OPTIONS[0].value, cId, {
        clearOnUnmount: true,
    });

    const [tableHeadersAndKeys, setTableHeadersAndKeys] = useState({
        headers: indivisualMarket.status ? lodashGet(HEADERS_KEYS, ['indivisualMarket', 'headers']) : lodashGet(HEADERS_KEYS, ['masterBidRequest', 'headers']),
        keys: indivisualMarket.status ? lodashGet(HEADERS_KEYS, ['indivisualMarket', 'keys']) : lodashGet(HEADERS_KEYS, ['masterBidRequest', 'keys']),
    });

    const [paginationQuery, setPaginationQuery] = useState({
        skip: 0,
        page: 0,
        limit: 15,
    });

    const [bidRequestData, setBidRequestData] = useState({
        request: [],
        totalCount: 0,
    });

    const [overviewModal, setOverviewModal] = useState({
        status: false,
        request: null,
    });
    const [deleteModal, setDeleteModal] = useState({ status: false, id: null });

    useEffect(() => {
        if (document.readyState === 'complete') setInitialLoad(true);
    }, []);

    useEffect(() => {
        if (indivisualMarket.status) setSelectedRegulation(indivisualMarket.market);
    }, [indivisualMarket.market]);

    useEffect(() => {
        if (isRootUser && !indivisualMarket.status) getAdmins();
        if (!indivisualMarket.status) getRegulation();
    }, []);

    useEffect(() => {
        if (selectedAdmin && selectedAdmin !== 'all' && !indivisualMarket.status) getCustomers();
        else if (selectedAdmin === 'all') setSelectedCustomer(DEFAULT_DROPDOWN_OPTION.value);
    }, [selectedAdmin]);

    useEffect(() => {
        if (indivisualMarket.status && selectedRegulation) getMarketEnabledCusLicenseIns();
    }, [selectedRegulation]);

    useEffect(() => {
        if (selectedCustomer && selectedRegulation) getBidsRequest();
    }, [selectedRegulation, selectedCustomer, selectedFilter]);

    const getAdmins = () => {
        AdminService.GetAdmins(startLoader, handleGetAdminSuccess, handleError, stopLoader);
    };

    const handleGetAdminSuccess = ({ data }) => {
        const options = getDropdownOption(data.data);
        if (!selectedAdmin) setSelectedAdmin(options[0].value);
        setAdmins(options);
    };

    const getRegulation = () => {
        const params = { date: momentTimeFormater(date).startOf('day').format() };
        RegulationService.GetRegulationSortedOnBids(params, startLoader, handleGetRegulationSuccess, handleError, stopLoader);
    };

    const handleGetRegulationSuccess = ({ data }) => {
        const options = getDropdownOption(data.data, false, 'name', 'name');
        if (!selectedRegulation) setSelectedRegulation(options[0].value);
        setRegulations(options);
    };

    const getCustomers = () => {
        const params = { adminId: selectedAdmin };
        CustomerService.GetCustomers(params, startLoader, handleGetCustomerSuccess, handleError, stopLoader);
    };

    const handleGetCustomerSuccess = ({ data }) => {
        const options = getDropdownOption(data.data);
        if (!selectedCustomer || initialLoad) setSelectedCustomer(options[0].value);
        setCustomers(options);
    };

    const getBidsRequest = () => {
        const params = {
            date: moment(date).format('YYYY-MM-DD'),
            market: selectedRegulation,
            adminId: selectedAdmin,
            customerId: selectedCustomer,
            skip: paginationQuery.skip,
            limit: paginationQuery.limit,
            ...(indivisualMarket.status ? { filter: selectedFilter } : {}),
        };
        service.GetBidRequest(params, startLoader, handleGetBidRequestSuccess, handleError, stopLoader);
    };

    const handleGetBidRequestSuccess = ({ data }) => {
        if (lodashGet(data, ['data', 'data']).length) {
            const processed = lodashGet(data, ['data', 'data'], []).map((el) => ({
                startDate: momentTimeFormater(el.startDate).format('YYYY-MM-DD'),
                endDate: momentTimeFormater(el.endDate).format('YYYY-MM-DD'),
                createdAt: momentTimeFormater(el.createdAt).format('YYYY-MM-DD'),
                updatedAt: momentTimeFormater(el.updatedAt).format('YYYY-MM-DD'),
                instance: lodashGet(el, ['instance', 'name']),
                customerName: lodashGet(el, ['customerName']),
                license: lodashGet(el, ['license', 'name']),
                status: el?.status ? Capitalize(el?.status) : '--',
                action: (
                    <div className={bidClasses.ActionContainer}>
                        <div onClick={() => updateBidRequestStatus(el._id)}>
                            <IconComponent icon={UilCheckCircle} tooltip={'Update'} />
                        </div>
                        <div onClick={() => setOverviewModal({ status: true, request: el })}>
                            <IconComponent icon={UilEye} tooltip={'View'} />
                        </div>
                        <div onClick={() => setDeleteModal({ status: true, id: el._id })}>
                            <IconComponent icon={UilTrash} tooltip={'Delete'} />
                        </div>
                    </div>
                ),
            }));
            setBidRequestData({ request: processed, totalCount: lodashGet(data, ['data', 'count']) });
        } else setBidRequestData({ request: [], totalCount: 0 });
    };

    const handleDelete = (id) => {
        service.DeleteBidRequest(id, startLoader, deleteSuccess, handleError, stopLoader);
    };

    const deleteSuccess = () => {
        toast.success('Bid request deleted successfully!');
        getBidsRequest();
    };

    const updateBidRequestStatus = (id) => {
        service.UpdateBidRequestStatus(id, startLoader, handleStatusUpdateSuccess, handleError, stopLoader);
    };

    const handleStatusUpdateSuccess = () => {
        toast.success('Bid status updated!');
        getBidsRequest();
    };

    const handlePageChange = (pageNo) => {
        setPaginationQuery({ ...paginationQuery, skip: paginationQuery.limit * pageNo, page: pageNo });
    };

    const handleError = (err) => {
        toast.error(lodashGet(err, ['response', 'data', 'message']));
    };

    const getMarketEnabledCusLicenseIns = () => {
        AdminBiddingService.GetMarketEnabledCusLicenseIns(selectedRegulation, startLoader, getMarketEnabledCusLicenseInsSuccess, handleError, stopLoader);
    };

    const getMarketEnabledCusLicenseInsSuccess = ({ data }) => {
        data = lodashGet(data, ['data'], {});
        const customers = lodashKeys(data);
        if (customers.length) {
            const customerOptions = customers.map((cusId) => ({
                label: lodashGet(data, [cusId, 'name']),
                value: cusId,
            }));
            setCustomers(customerOptions);
            if (!selectedCustomer || initialLoad) setSelectedCustomer(lodashGet(customerOptions, [0, 'value']));
        } else toast.error('No Instances Enabled');
    };

    return (
        <div>
            <div className={FilterBarClasses.ParentWrapper} style={{ alignItems: 'center' }}>
                <div className={FilterBarClasses.Left}>
                    {isRootUser && (
                        <div className={FilterBarClasses.Filter}>
                            <label>Admin</label>
                            <div className={bidClasses.DropdownContainer}>
                                <DropdownComponent
                                    defaultValue={{ value: selectedAdmin }}
                                    options={admins}
                                    onChange={(e) => {
                                        setSelectedAdmin(e.value);
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    <div className={FilterBarClasses.Filter}>
                        <label>Customer</label>
                        <div className={bidClasses.DropdownContainer}>
                            <DropdownComponent
                                defaultValue={{ value: selectedCustomer }}
                                options={customers}
                                onChange={(e) => {
                                    setSelectedCustomer(e.value);
                                }}
                            />
                        </div>
                    </div>

                    {/* market and filter for master bids */}
                    {!indivisualMarket.status && (
                        <div className={FilterBarClasses.Filter}>
                            <label>{'Market'}</label>
                            <div className={bidClasses.DropdownContainer}>
                                <DropdownComponent defaultValue={{ value: selectedRegulation }} options={regulations} onChange={(e) => setSelectedRegulation(e.value)} />
                            </div>
                        </div>
                    )}

                    {indivisualMarket.status && (
                        <div className={FilterBarClasses.Filter}>
                            <label> {'Filter'} </label>
                            <div className={bidClasses.DropdownContainer}>
                                <DropdownComponent defaultValue={{ value: selectedFilter }} options={FILTER_OPTIONS} onChange={(e) => setSelectedFilter(e.value)} />
                            </div>
                        </div>
                    )}
                </div>
                <div className={FilterBarClasses.Right} style={{ alignItems: 'center' }}>
                    {!indivisualMarket.status && (
                        <>
                            <div className={FilterBarClasses.Filter}>
                                {
                                    <>
                                        <label>Date</label>
                                        <DatePicker
                                            date={date}
                                            setDate={(d) => {
                                                setDate(d);
                                            }}
                                            showArrow={true}
                                            disableFutureDate={false}
                                        />
                                    </>
                                }
                            </div>
                            <button className="btn-primary" style={{ width: 'auto' }} onClick={getBidsRequest}>
                                Submit
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className={bidClasses.MainContainer}>
                <div className={bidClasses.TableHeader}>
                    <div>
                        <Typography content={'Bid Requests'} />
                        <div className={bidClasses.TableCount}>
                            Total Count :
                            <span>
                                <Typography size="14" content={bidRequestData.totalCount} />
                            </span>
                        </div>
                    </div>
                </div>
                <Table
                    head={tableHeadersAndKeys.headers}
                    keys={tableHeadersAndKeys.keys}
                    data={bidRequestData.request}
                    page={paginationQuery.page}
                    Pagination={true}
                    limit={paginationQuery.limit}
                    handlePageChange={handlePageChange}
                    totalCount={bidRequestData.totalCount}
                />
            </div>
            <ModalComponent isOpen={overviewModal.status} setOpen={() => setOverviewModal({ status: false, request: null })}>
                {overviewModal.status && <RequestOverview market={selectedRegulation} request={overviewModal.request} setOpen={setOverviewModal} />}
            </ModalComponent>
            <DeleteModal
                deletefunction={handleDelete}
                opendeleteModal={deleteModal.status}
                setOpenDeleteModal={(status) => setDeleteModal({ id: null, status })}
                deviceId={deleteModal.id}
            ></DeleteModal>
        </div>
    );
};
